import React from 'react';
import { useKioskVertretungsplanData } from '../hooks/useKioskVertretungsplanData';
import { useKioskDataProcessing } from '../hooks/useKioskDataProcessing';
import { useAutoScroll } from '../hooks/useAutoScroll';
import { useKioskHeartbeat } from '../hooks/useKioskHeartbeat';
import { useKioskTokenRefresh } from '../hooks/useKioskTokenRefresh';
import { useKioskFontScale } from '../hooks/useKioskFontScale';
import { KioskHeader } from './KioskHeader';
import { KioskAbsenceList } from './KioskAbsenceList';
import { KioskSubstitutionTable } from './KioskSubstitutionTable';
import { Button } from '../../../components/ui/button';

export default function KioskVertretungsplanLayout({ kioskInfo }: { kioskInfo?: any }) {
    // Kiosk usually shows "today" or "next school day". 
    const [currentDate, setCurrentDate] = React.useState(new Date());

    const { data, loading, error, refetch } = useKioskVertretungsplanData(currentDate);

    // Send heartbeat every 30 seconds to update monitor status
    // The monitor_id is extracted from the kiosk JWT by the hook
    useKioskHeartbeat(true, 30000);

    // Auto-refresh JWT token before expiration (checks hourly, refreshes when < 24h remaining)
    useKioskTokenRefresh(true);

    // Check for date change every minute and update currentDate when a new day begins
    React.useEffect(() => {
        const checkDateChange = () => {
            const now = new Date();
            const currentDateStr = currentDate.toDateString();
            const nowDateStr = now.toDateString();
            
            if (currentDateStr !== nowDateStr) {
                console.log('[KIOSK] Date changed from', currentDateStr, 'to', nowDateStr);
                setCurrentDate(now);
            }
        };

        // Check immediately on mount
        checkDateChange();

        // Check every minute for date changes
        const dateCheckInterval = setInterval(checkDateChange, 60 * 1000);

        return () => clearInterval(dateCheckInterval);
    }, [currentDate]);

    // Auto-refresh data every 30 seconds
    React.useEffect(() => {
        const interval = setInterval(() => {
            refetch(true);
        }, 30 * 1000);
        return () => clearInterval(interval);
    }, [refetch]);

    // Data Processing
    const {
        allEntries,
        absenteLehrkraefte,
        absenteKlassen,
        absentTagByKurzel,
        displayKurzel
    } = useKioskDataProcessing(data);

    // Calculate dynamic font scale based on content density
    const fontScale = useKioskFontScale(allEntries.length, absenteLehrkraefte.length);

    // Auto-Scroll
    const scrollRef = React.useRef<HTMLDivElement>(null);
    useAutoScroll(scrollRef, [allEntries]);

    // --- Render ---

    // Auth Check: Redirect to setup if no token
    React.useEffect(() => {
        const token = localStorage.getItem('fw_kiosk_jwt');
        if (!token) {
            window.location.replace('/kiosk/setup');
        }
    }, []);

    // Also redirect if the hook reports a missing/invalid session
    React.useEffect(() => {
        if (error && (String(error).includes('missing JWT') || String(error).includes('session invalid'))) {
            window.location.replace('/kiosk/setup');
        }
    }, [error]);

    if (loading && !data.lessons?.length) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center text-slate-900">
                <div className="flex flex-col items-center gap-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-4 border-slate-900"></div>
                    <div className="font-medium kiosk-text-lg">Lade Vertretungsplan...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center text-red-700">
                <div className="text-center space-y-4">
                    <div className="kiosk-text-2xl font-bold">Fehler beim Laden</div>
                    <div className="kiosk-text-lg font-medium">{String(error)}</div>
                    <div className="kiosk-text-sm opacity-75">Versuche Neustart oder Neuanmeldung...</div>
                    <Button onClick={() => window.location.replace('/kiosk/setup')} variant="outline" className="mt-4 border-slate-900 text-slate-900 hover:bg-slate-100">Zum Setup</Button>
                </div>
            </div>
        );
    }

    return (
        <div 
            className="fixed inset-0 overflow-hidden bg-white kiosk-container"
            style={{ '--kiosk-scale': fontScale } as React.CSSProperties}
        >
            <div className="flex flex-col md:flex-row gap-4 p-4 text-slate-900 h-full">

                {/* LEFT SIDEBAR */}
                <div className="w-full md:w-[18%] flex flex-col gap-4 h-full shrink-0">
                    <KioskHeader
                        logoUrl={data?.metadata?.school_logo_url}
                        currentDate={currentDate}
                    />
                    <KioskAbsenceList
                        absenteLehrkraefte={absenteLehrkraefte}
                        absenteKlassen={absenteKlassen}
                        absentTagByKurzel={absentTagByKurzel}
                        displayKurzel={displayKurzel}
                    />
                </div>

                {/* RIGHT CONTENT */}
                <div
                    ref={scrollRef}
                    className="w-full md:w-[82%] h-full overflow-y-auto kiosk-no-scrollbar"
                >
                    <KioskSubstitutionTable
                        entries={allEntries}
                        title="Vertretungen"
                        emptyMsg="Keine Vertretungen für heute."
                        displayKurzel={displayKurzel}
                    />
                </div>
                <style>{`
                    #sentry-feedback,
                    #sentry-feedback-button,
                    .sentry-feedback-widget,
                    .sentry-feedback-button,
                    [id^="sentry-feedback"],
                    [class*="sentry-feedback"],
                    [aria-label="Report a Bug"],
                    [aria-label="Bug melden"] {
                        display: none !important;
                        visibility: hidden !important;
                        opacity: 0 !important;
                        pointer-events: none !important;
                        z-index: -9999 !important;
                    }
                `}</style>
            </div>
        </div>
    );
}
